import type { AssetInstructionOut } from "kubb";
import type React from "react";
import { TableCell, TableRow } from "../../../../components/Theme/table";
import { formatDate } from "../../../../components/utils/HighlightNullAndOldDates";
import ChildAssetLink from "./ChildAssetLink";
import {
  formatInstructionType,
  formatMethod,
  formatStatus,
  formatTrigger,
  getRelayActionInfo,
  getStatusClasses,
  getSyncTypeInfo,
  isAwaitingDevice,
  shouldShowCountdown,
  useCountdown,
} from "./instructionFormatters";

interface Props {
  instruction: AssetInstructionOut;
  viewedAssetId: string;
  companyId: string;
}

// Re-export for backwards compatibility with existing imports
export { getSyncTypeInfo, getRelayActionInfo } from "./instructionFormatters";

export default function AssetInstructionRow(props: Props): React.ReactElement {
  const { instruction, viewedAssetId, companyId } = props;
  const countdown = useCountdown(instruction.scheduled_at);
  const showCountdown =
    countdown &&
    shouldShowCountdown(instruction.status, instruction.configuration_method);
  const awaitingDevice = isAwaitingDevice(
    instruction.status,
    instruction.configuration_method,
  );
  const syncTypeInfo = getSyncTypeInfo(instruction);
  const relayActionInfo = getRelayActionInfo(instruction);

  // Determine if this is a child device instruction shown on gateway view
  const isChildDeviceInstruction =
    instruction.parent_asset_id &&
    instruction.parent_asset_id === viewedAssetId &&
    instruction.asset_id !== viewedAssetId;

  return (
    <TableRow key={instruction.id}>
      <TableCell className="align-top">
        <div className="flex items-center gap-2">
          <span className="inline-flex w-5 justify-center text-gray-400">
            •
          </span>
          <span>{formatInstructionType(instruction.instruction_type)}</span>
          {syncTypeInfo && (
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${syncTypeInfo.classes}`}
              title={
                syncTypeInfo.label === "Targeted"
                  ? "Targeted sync: Only changed values will be sent"
                  : "Full sync: All configuration values will be sent"
              }
            >
              {syncTypeInfo.label}
            </span>
          )}
          {relayActionInfo && (
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${relayActionInfo.classes}`}
              title={relayActionInfo.tooltip}
            >
              {relayActionInfo.label}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        {isChildDeviceInstruction ? (
          <ChildAssetLink
            assetId={instruction.asset_id}
            companyId={companyId}
          />
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </TableCell>
      <TableCell className="align-top">
        {formatMethod(instruction.configuration_method)}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-1">
          <span className={getStatusClasses(instruction.status)}>
            {formatStatus(instruction.status)}
          </span>
          {showCountdown && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {countdown}
            </span>
          )}
          {awaitingDevice && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Awaiting device
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        {formatTrigger(instruction.trigger)}
      </TableCell>
      <TableCell className="align-top">
        {instruction.user_email || "System"}
      </TableCell>
      <TableCell className="align-top">
        {instruction.current_attempt}/{instruction.max_attempts}
      </TableCell>
      <TableCell className="align-top">
        <span className="text-nowrap">
          {formatDate(instruction.created_at)}
        </span>
      </TableCell>
      <TableCell className="align-top">
        <span className="text-nowrap">
          {formatDate(instruction.updated_at)}
        </span>
      </TableCell>
    </TableRow>
  );
}
